import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { Button, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, Grid, TextField } from '@mui/material';

import { Constant } from '@/types';
import { useUpdateConstantMutation } from '@/services';
import { messages } from '@/utils/helpers';

export interface UpdateConstantProps extends Omit<DialogProps, 'constant'> {
  constant: Constant;
  onClose(): void;
}

const UpdateConstant: React.FC<UpdateConstantProps> = ({ constant, onClose, ...props }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [updateConstant, updateConstantResponse] = useUpdateConstantMutation();

  const { values, errors, touched, handleSubmit, handleChange } = useFormik<Constant>({
    initialValues: constant ?? {
      name: '',
    },
    enableReinitialize: true,
    validationSchema: yup.object().shape({
      name: yup.string().required(messages.form.REQUIRED),
    }),
    async onSubmit(constant, { resetForm }) {
      try {
        await updateConstant(constant).unwrap();

        onClose();
        resetForm();
      } catch (error) {
        enqueueSnackbar(error.data, { variant: 'error' });
      }
    },
  });

  return (
    <Dialog fullWidth {...props}>
      <DialogTitle fontWeight="bold">Редактировать константу</DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item lg={6} md={6} xs={12}>
              <TextField
                name="name"
                label="Наименование"
                placeholder="Введите значение"
                value={values.name}
                onChange={handleChange}
                disabled={updateConstantResponse.isLoading}
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name ? errors.name : ''}
                fullWidth
              />
            </Grid>

            <Grid item lg={6} md={6} xs={12}>
              <TextField
                name="code"
                label="Код"
                placeholder="Введите значение"
                value={values.code}
                onChange={handleChange}
                disabled={updateConstantResponse.isLoading}
                error={touched.code && Boolean(errors.code)}
                helperText={touched.code ? errors.code : ''}
                fullWidth
              />
            </Grid>

            <Grid item lg={6} md={6} xs={12}>
              <TextField
                name="value"
                label="Значение"
                placeholder="Введите значение"
                value={values.value}
                onChange={handleChange}
                disabled={updateConstantResponse.isLoading}
                error={touched.value && Boolean(errors.value)}
                helperText={touched.value ? errors.value : ''}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button
            type="button"
            color="secondary"
            variant="outlined"
            disabled={updateConstantResponse.isLoading}
            onClick={onClose}
          >
            Закрыть
          </Button>

          <Button type="submit" variant="contained" disabled={updateConstantResponse.isLoading}>
            Редактировать
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UpdateConstant;
